import { useState, ReactNode, useEffect } from "react";
import {
  Offcanvas,
  Button,
  Form,
  Badge,
  Card,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faTimes,
  faChevronLeft,
  faTrash,
  faCheck,
  faUserSlash,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

// Interface genérica para filtros
export interface FilterOption {
  id: string;
  label: string;
  description?: string;
  icon?: any;
  type: 'checkbox' | 'radio' | 'select' | 'date' | 'text' | 'radioCard';
  options?: {value: string, label: string, description?: string, icon?: any}[];
  value?: any;
}

interface FilterOffCanvasProps {
  show: boolean;
  onHide: () => void;
  onApplyFilters: (filters: Record<string, any>) => void;
  title: string;
  filterOptions: FilterOption[];
  activeFilters: Record<string, any>;
  children?: ReactNode;
}

const FilterOffCanvas = ({
  show,
  onHide,
  onApplyFilters,
  title,
  filterOptions,
  activeFilters,
  children,
}: FilterOffCanvasProps) => {
  const [localFilters, setLocalFilters] = useState<Record<string, any>>(activeFilters || {});
  
  // Initialize localFilters when activeFilters changes
  useEffect(() => {
    if (show) {
      setLocalFilters(activeFilters || {});
    }
  }, [activeFilters, show]);
  
  // Conta quantos filtros estão ativos
  const activeFilterCount = Object.keys(localFilters).filter(key => 
    localFilters[key] !== undefined && 
    localFilters[key] !== '' && 
    localFilters[key] !== false && 
    localFilters[key] !== 'active').length;

  const handleFilterChange = (id: string, value: any) => {
    setLocalFilters(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleApplyFilters = () => {
    onApplyFilters(localFilters);
    onHide();
  };

  const handleClearFilters = () => {
    // Reset to default values (active users)
    const defaultFilters = { userStatus: 'active' };
    setLocalFilters(defaultFilters);
    onApplyFilters(defaultFilters);
  };

  const renderFilterControl = (filter: FilterOption) => {
    switch(filter.type) {
      case 'radioCard':
        return (
          <div className="mb-4">
            <Form.Label className="mb-2 fs-9 text-body-tertiary">{filter.label}</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {filter.options?.map(option => {
                const isSelected = localFilters[filter.id] === option.value;
                return (
                  <Card 
                    key={option.value} 
                    className={`filter-card cursor-pointer border shadow-sm flex-grow-1 ${isSelected ? 'border-primary' : ''}`}
                    style={{ 
                      minWidth: '110px',
                      maxWidth: '110px',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleFilterChange(filter.id, option.value)}
                  >
                    <Card.Body className="p-3 text-center">
                      <div className="d-inline-flex align-items-center justify-content-center mb-2" 
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: isSelected ? 'var(--phoenix-primary-bg-subtle)' : '#f8f9fa',
                          color: isSelected ? 'var(--phoenix-primary)' : '#6c757d',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {option.icon ? (
                          <FontAwesomeIcon icon={option.icon} size="lg" />
                        ) : null}
                      </div>
                      <p className="mb-0 fw-semibold" style={{ fontSize: '0.9rem' }}>{option.label}</p>
                      {option.description && (
                        <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                          {option.description}
                        </small>
                      )}
                      {isSelected && (
                        <div 
                          className="position-absolute" 
                          style={{ top: '10px', right: '10px' }}
                        >
                          <FontAwesomeIcon 
                            icon={faCheck} 
                            className="text-primary" 
                            style={{ fontSize: '0.8rem' }} 
                          />
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      
      case 'checkbox':
        return (
          <Form.Check 
            type="checkbox"
            id={filter.id}
            label={filter.label}
            checked={!!localFilters[filter.id]}
            onChange={(e) => handleFilterChange(filter.id, e.target.checked)}
            className="mb-3"
          />
        );
      
      case 'select':
        return (
          <Form.Group className="mb-3">
            <Form.Label>{filter.label}</Form.Label>
            <Form.Select
              value={localFilters[filter.id] || ''}
              onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            >
              <option value="">Todos</option>
              {filter.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        );
      
      case 'date':
        return (
          <Form.Group className="mb-3">
            <Form.Label>{filter.label}</Form.Label>
            <Form.Control
              type="date"
              value={localFilters[filter.id] || ''}
              onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            />
          </Form.Group>
        );

      case 'text':
      default:
        return (
          <Form.Group className="mb-3">
            <Form.Label>{filter.label}</Form.Label>
            <Form.Control
              type="text"
              placeholder={`Filtrar por ${filter.label.toLowerCase()}`}
              value={localFilters[filter.id] || ''}
              onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            />
          </Form.Group>
        );
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="filter-offcanvas"
      style={{ width: "450px", maxWidth: "100%" }}
    >
      <Offcanvas.Header className="py-3 bg-body" style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <div className="d-flex align-items-center w-100 justify-content-between">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              className="me-2 p-1 text-body-secondary border-0"
              onClick={onHide}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <Offcanvas.Title className="ms-1 h5 mb-0 fw-semibold d-flex align-items-center">
              <FontAwesomeIcon icon={faFilter} className="me-2 text-primary" />
              {title}
            </Offcanvas.Title>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0 d-flex flex-column" style={{ height: 'calc(100% - 60px)' }}>
        <div className="p-4 flex-grow-1 overflow-auto">
          <p className="text-muted fw-light mb-4">
            Defina os filtros para exibir usuários ativos, excluídos ou ambos.
          </p>

          {activeFilterCount > 0 && (
            <div className="mb-4">
              <Badge
                bg="light"
                text="dark"
                className="d-inline-flex align-items-center py-2 px-3 border shadow-sm bg-body"
                style={{ borderRadius: "50px" }}
              >
                <span className="text-body-secondary">
                  {activeFilterCount}{" "}
                  {activeFilterCount === 1
                    ? "filtro ativo"
                    : "filtros ativos"}
                </span>
                <Button
                  variant="link"
                  className="p-0 ms-2 text-body-secondary border-0"
                  onClick={handleClearFilters}
                  aria-label="Limpar todos os filtros"
                  style={{ fontSize: "14px" }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </Badge>
            </div>
          )}

          <Form>
            {filterOptions.map((filter) => (
              <div key={filter.id} className="mb-3">
                {renderFilterControl(filter)}
              </div>
            ))}
            
            {children}
          </Form>
        </div>

        {/* Fixed footer with action buttons */}
        <div className="mt-auto p-3 border-top bg-body" style={{ background: '#f8f9fa' }}>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              className="d-flex align-items-center justify-content-center w-100 py-3"
              onClick={handleClearFilters}
            >
              <FontAwesomeIcon icon={faTrash} className="me-2" />
              Redefinir Filtros
            </Button>
            <Button 
              variant="primary" 
              className="d-flex align-items-center justify-content-center w-100 py-3"
              onClick={handleApplyFilters}
            >
              <FontAwesomeIcon icon={faFilter} className="me-2" />
              Aplicar Filtros
            </Button>
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default FilterOffCanvas;
